import { AxiosError } from 'axios'

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'

import { ClientInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface BusinessClientsQueryParams {
  all_data?: boolean
  page?: number
  per_page?: number
  orderBy?: string
  order?: string
  search?: string
  customerId?: number
}

// Create a unique, descriptive key for this query
const BUSINESS_CLIENTS_QUERY_KEY = 'business-clients'

interface BusinessClientsResponse {
  items: ClientInterface[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Interface for the query key parameters
interface BusinessClientsQueryKeyParams {
  businessId?: number
  search?: string
  orderBy?: string
  order?: string
  customerId?: number
}

// Interface for the hook parameters
interface UseBusinessClientsQueryParams {
  params?: BusinessClientsQueryParams
  options?: Partial<UseQueryOptions<BusinessClientsResponse, AxiosError>>
  businessId?: number
}

// Interface for the infinite query hook parameters
interface UseInfiniteBusinessClientsQueryParams {
  businessId?: number
  params?: BusinessClientsQueryParams
  options?: Partial<
    UseInfiniteQueryOptions<
      BusinessClientsResponse,
      AxiosError,
      BusinessClientsResponse,
      BusinessClientsResponse,
      [string, BusinessClientsQueryKeyParams],
      number
    >
  >
}

// Interface for the return type of useBusinessClientsQuery
interface UseBusinessClientsQueryReturn {
  businessClients: ClientInterface[] | undefined
  isLoadingBusinessClients: boolean
  isErrorBusinessClients: boolean
  errorBusinessClients: AxiosError | null
  refetchBusinessClients: () => Promise<unknown>
  isFetchingBusinessClients: boolean
  totalBusinessClients: number
  currentPage: number
  totalPages: number
}

// Interface for the return type of useInfiniteBusinessClientsQuery
interface UseInfiniteBusinessClientsQueryReturn {
  businessClients: ClientInterface[]
  totalItems: number
  loadedCount: number
  hasNextPage: boolean | undefined
  isLoading: boolean
  isLoadingMore: boolean
  isError: boolean
  error: AxiosError | null
  fetchNextPage: () => Promise<unknown>
  refetch: () => Promise<unknown>
  isFetching: boolean
}

/**
 * Fetch business clients from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessClients = async (
  params?: BusinessClientsQueryParams
): Promise<BusinessClientsResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/invoice/customers/all', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching business clients
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @param businessId - Business ID for scoping
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessClientsQuery = ({
  params,
  options,
  businessId,
}: UseBusinessClientsQueryParams): UseBusinessClientsQueryReturn => {
  // Query hook
  const queryResult = useQuery<BusinessClientsResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: getBusinessClientsQueryKey({
      search: params?.search ?? '',
      orderBy: params?.orderBy ?? '',
      order: params?.order ?? '',
      customerId: params?.customerId ?? undefined,
      businessId,
    }),

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessClients(params),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    businessClients: queryResult.data?.items,
    isLoadingBusinessClients: queryResult.isLoading,
    isErrorBusinessClients: queryResult.isError,
    errorBusinessClients: queryResult.error,
    refetchBusinessClients: queryResult.refetch,
    isFetchingBusinessClients: queryResult.isFetching,
    totalBusinessClients: queryResult.data?.totalItems || 0,
    currentPage: queryResult.data?.currentPage || 1,
    totalPages: queryResult.data?.totalPages || 1,
  }
}

/**
 * Custom hook for infinite scrolling business clients with search functionality
 * Following TanStack Query's infinite query pattern with optimized caching
 *
 * @param businessId - Business ID for scoping
 * @param params - Query parameters including search
 * @param options - TanStack Query options for customizing query behavior
 * @returns Object containing clients, loading states, and utility functions
 */
export const useInfiniteBusinessClientsQuery = ({
  businessId,
  params = {},
  options,
}: UseInfiniteBusinessClientsQueryParams): UseInfiniteBusinessClientsQueryReturn => {
  const queryResult = useInfiniteQuery<
    BusinessClientsResponse,
    AxiosError,
    BusinessClientsResponse,
    [string, BusinessClientsQueryKeyParams],
    number
  >({
    queryKey: getBusinessClientsQueryKey({
      search: params?.search ?? '',
      orderBy: params?.orderBy ?? '',
      order: params?.order ?? '',
      businessId,
    }),
    queryFn: async ({ pageParam }) => {
      const result = await fetchBusinessClients({
        ...params,
        page: pageParam,
        per_page: 20,
      })
      return result
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined
    },
    refetchOnWindowFocus: false,

    // Keep data fresh for 3 minutes (clients change more frequently than products)
    staleTime: 3 * 60 * 1000, // 3 minutes

    // Keep inactive data in cache for 15 minutes (good for client searches)
    gcTime: 15 * 60 * 1000, // 15 minutes

    // Keep previous data while fetching new search results (better UX)
    placeholderData: (previousData) => previousData,

    // Retry failed requests (network resilience)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Apply any custom options
    ...options,
  })

  // Flatten all pages into a single array
  const allClients =
    (
      queryResult.data as { pages: BusinessClientsResponse[] } | undefined
    )?.pages?.flatMap((page: BusinessClientsResponse) => page?.items || []) ||
    []

  // Get total from first page (all pages have same total)
  const totalItems =
    (queryResult.data as { pages: BusinessClientsResponse[] } | undefined)
      ?.pages?.[0]?.totalItems || 0

  return {
    businessClients: allClients,
    totalItems,
    loadedCount: allClients?.length || 0,
    hasNextPage: queryResult.hasNextPage,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isFetchingNextPage,
    isError: queryResult.isError,
    error: queryResult.error,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
    isFetching: queryResult.isFetching,
  }
}

/**
 * Helper function to get the business clients query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessClientsQueryKey = ({
  businessId,
  search,
  orderBy,
  order,
  customerId,
}: BusinessClientsQueryKeyParams): [string, BusinessClientsQueryKeyParams] => [
  BUSINESS_CLIENTS_QUERY_KEY,
  { search, orderBy, order, businessId, customerId },
]
